import React, { ChangeEvent, JSX, useEffect, useState } from "react";
import { OwFnInput } from "../OwFnInput/OwFnInput";
import { v4 } from "uuid";
import styled from "styled-components";

type Props = {
    /**
     * The lookup function for using the value of the input to find the options
     * @param value
     */
    lookupFn: (value: string) => void;
    /**
     * The options that are available to the user to select from
     */
    availableOptions: any[];
    /**
     * The selected option
     */
    selectedOption?: any;
    /**
     * The selected option function in which the selected item is emitted to
     * @param value
     */
    selectedOptionFn: (value: any) => void;
    /**
     * Configure how the selection should be emitted to the selectedOptionFn
     */
    emitSelectionSetup?: {
        /**
         * Emits the entire item object (ignores the valueProp)
         */
        emitObject?: boolean;
        /**
         * Emits the items value, based on the valueProp
         */
        emitValue?: boolean;
    };
    /**
     * The title value for the item to show
     */
    titleProp?: string;
    /**
     * The sub title value for the item to show
     */
    subTitleProp?: string;
    /**
     * The value property for the item
     */
    valueProp?: string;
    /**
     * The render function for the item (if need custom setup)
     * @param item
     */
    renderFn?: (item: any) => JSX.Element;
    maxHeight?: number;
    isScrollable?: boolean;
    placeholder?: string;
    /**
     * A flag to signal resetting the typeahead. Must be used with the callback fn.
     */
    resetTypeahead?: boolean;
    resetTypeaheadCallbackFn?: () => void;
    clearSelectionFn?: () => void;
    wrapperClassName?: string;
    inputClassName?: string;

    disabled?: boolean;
};

export const TypeaheadFn = (props: Props) => {
    const [showOptions, setShowOptions] = useState(false);
    const [inputVal, setInputVal] = useState("");
    const [selectedOption, setSelectedOption] = useState<any>(props.selectedOption);

    useEffect(() => {
        if (props.availableOptions.length) {
            setShowOptions(true);
        }
    }, [props.availableOptions]);

    useEffect(() => {
        if (props.resetTypeahead && props.resetTypeaheadCallbackFn) {
            setShowOptions(false);
            setInputVal("");
            setSelectedOption(undefined);
            // props.resetTypeaheadCallbackFn();
        }
    }, [props.resetTypeahead]);

    useEffect(() => {
        if (props.selectedOption) {
            if (props.titleProp) {
                setInputVal(props.selectedOption[props.titleProp]);
            }
        } else {
            setInputVal("");
        }
    }, [props.selectedOption]);

    const handleInput = (e: ChangeEvent<any>) => {
        // Clear reset flag first if active
        if (props.resetTypeahead && props.resetTypeaheadCallbackFn) {
            props.resetTypeaheadCallbackFn();
        }

        // Always process input normally
        const value = e.target.value;
        if (value !== inputVal) {
            props.lookupFn(value);
            setInputVal(value);
        }
    };

    const emitSelectedOption = (option: any) => {
        if (props.emitSelectionSetup) {
            if (props.emitSelectionSetup.emitObject) {
                props.selectedOptionFn(option);
            } else if (props.emitSelectionSetup.emitValue) {
                // @ts-expect-error
                props.selectedOptionFn(option[props.valueProp]);
            }
        } else {
            props.selectedOptionFn(option);
        }

        setShowOptions(false);
        setSelectedOption(option);

        if (props.titleProp) {
            setInputVal(option[props.titleProp]);
        }
    };

    const clearSelection = () => {
        if (props.clearSelectionFn) {
            props.clearSelectionFn();
        }
    };

    return (
        <div className={"TypeaheadFn position-relative " + (props.wrapperClassName || "")}>
            <OwFnInput
                fn={handleInput}
                name={"typeahead"}
                enableDebounce={true}
                value={inputVal}
                noOuterDiv={true}
                placeholder={props.placeholder}
                className={props.inputClassName}
                disabled={props.disabled}
            />
            {props.selectedOption && props.clearSelectionFn && !props.disabled ? (
                <ClearDiv>
                    <i className={"fas fa-times pointer"} onClick={clearSelection} />
                </ClearDiv>
            ) : null}
            {(props.availableOptions.length || []) && showOptions ? (
                <OptionsWrapper>
                    {props.availableOptions.map((option) => {
                        return (
                            <div key={v4()}>
                                {props.renderFn ? (
                                    <Option
                                        onClick={() => {
                                            emitSelectedOption(option);
                                        }}>
                                        {props.renderFn(option)}
                                    </Option>
                                ) : (
                                    <Option
                                        onClick={() => {
                                            emitSelectedOption(option);
                                        }}>
                                        <div>{props.titleProp ? option[props.titleProp] : option}</div>
                                        {props.subTitleProp ? <div>{option[props.subTitleProp]}</div> : null}
                                    </Option>
                                )}
                            </div>
                        );
                    })}
                </OptionsWrapper>
            ) : null}
        </div>
    );
};

const OptionsWrapper = styled.div`
    background-color: var(--main-bg-color);
    position: absolute;
    width: 100%;
    display: flex;
    flex-direction: column;
    font-size: 0.875rem;
    box-shadow: 0px 3px 3px 0px rgba(0, 0, 0, 0.34);
    //border: var(--bs-border-width) solid var(--bs-border-color);
    border-radius: var(--bs-border-radius-sm);
    z-index: 2;
`;

const Option = styled.div`
    width: 100%;
    padding: 0.25rem 0.5rem;
    line-height: 2.75rem;
    font-size: 1rem;

    &:hover {
        cursor: pointer;
        background-color: var(--table-hover-row);
    }
`;

const ClearDiv = styled.div`
    position: absolute;
    right: 0;
    top: 0;
    margin-right: 0.5rem;
    height: 31px;
    line-height: 31px;
`;
